import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { ArrowLeft, BookOpen, Flame, Rocket, Heart, ArrowRight } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_product-lineup/artifacts/0s0pdb47_ChatGPT%20Image%20Apr%203%2C%202026%2C%2011_16_10%20AM.png";

export default function FounderPage() {
  return (
    <div className="min-h-screen bg-slate-950 text-white grain-overlay">
      <Header />

      {/* Breadcrumb */}
      <div className="mx-auto max-w-5xl px-6 pt-8">
        <Link
          to="/"
          data-testid="back-to-home"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-white transition-colors"
        >
          <ArrowLeft size={14} />
          Back to home
        </Link>
      </div>

      {/* Hero Section */}
      <section data-testid="founder-hero" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_-10%,rgba(234,179,8,0.06),transparent)]" />
        <div className="mx-auto max-w-5xl px-6 pt-12 pb-16">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-8 mb-12">
            {/* Logo */}
            <div
              data-testid="founder-logo"
              className="shrink-0 w-32 h-32 md:w-40 md:h-40 rounded-2xl bg-white p-3 shadow-2xl shadow-amber-950/20 border border-white/20"
            >
              <img
                src={LOGO_URL}
                alt="Perfect Peace Enterprise Logo"
                className="w-full h-full object-contain"
              />
            </div>

            <div>
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-amber-400 mb-3">
                CEO & Founder
              </p>
              <h1
                data-testid="founder-name"
                className="text-4xl md:text-5xl font-bold tracking-tight"
              >
                Richard
              </h1>
              <p className="mt-3 text-lg text-slate-400 max-w-2xl">
                Founder and CEO of Perfect Peace Enterprise — a visionary builder driven by
                real-world experience and an unrelenting commitment to solving everyday problems
                through technology.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story */}
      <section data-testid="founder-story" className="border-y border-white/[0.06] bg-white/[0.015]">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <div className="max-w-3xl space-y-8">
            {/* Origin */}
            <div className="flex gap-5">
              <div className="shrink-0 mt-1">
                <div className="inline-flex rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-3">
                  <Flame size={20} className="text-amber-400" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-3">The Origin</h2>
                <p className="text-sm leading-8 text-slate-400">
                  Before stepping fully into entrepreneurship, Richard served in the fire department,
                  where he witnessed firsthand the urgent gaps in safety, communication, and preparedness
                  that affect ordinary people in critical moments. Those experiences did not fade when he
                  left — they became the foundation of his mission.
                </p>
              </div>
            </div>

            {/* From Author to Builder */}
            <div className="flex gap-5">
              <div className="shrink-0 mt-1">
                <div className="inline-flex rounded-xl border border-cyan-400/20 bg-cyan-400/[0.06] p-3">
                  <BookOpen size={20} className="text-cyan-300" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-3">From Author to Builder</h2>
                <p className="text-sm leading-8 text-slate-400">
                  His journey into innovation began with authorship. After writing{" "}
                  <em className="text-white/80">Island Roots in the Arctic Alaska</em>, a work that
                  reflects resilience, identity, and adaptation in extreme environments, Richard
                  transformed storytelling into functionality. He brought the book to life through
                  technology by developing the FAN (First Aid Network) app — a platform designed to
                  provide accessible, life-saving information when it matters most.
                </p>
                <p className="mt-4 text-sm leading-8 text-slate-400">
                  That first build marked a turning point.
                </p>
              </div>
            </div>

            {/* The Mission */}
            <div className="flex gap-5">
              <div className="shrink-0 mt-1">
                <div className="inline-flex rounded-xl border border-emerald-400/20 bg-emerald-400/[0.06] p-3">
                  <Rocket size={20} className="text-emerald-400" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-3">The Mission</h2>
                <p className="text-sm leading-8 text-slate-400">
                  Richard recognized that the world is filled with overlooked problems waiting for
                  practical solutions. Rather than stopping at one idea, he committed himself to
                  systematically identifying real-world challenges and addressing them through mobile
                  and web applications. Each product under Perfect Peace Enterprise is rooted in purpose —
                  designed not just to exist, but to serve, protect, and empower.
                </p>
                <p className="mt-4 text-sm leading-8 text-slate-400">
                  His approach blends lived experience with forward-thinking execution, creating tools
                  that bridge the gap between awareness and action. Whether improving safety, enhancing
                  daily productivity, or reimagining how people interact with technology, Richard builds
                  with intention.
                </p>
              </div>
            </div>

            {/* Today */}
            <div className="flex gap-5">
              <div className="shrink-0 mt-1">
                <div className="inline-flex rounded-xl border border-amber-400/20 bg-amber-400/[0.06] p-3">
                  <Heart size={20} className="text-amber-400" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-3">Today</h2>
                <p className="text-sm leading-8 text-slate-400">
                  Through Perfect Peace Enterprise, he continues to expand a growing ecosystem of
                  solutions — each one reflecting his core belief:
                </p>
                <blockquote
                  data-testid="founder-quote"
                  className="mt-4 pl-5 border-l-2 border-amber-400/30 text-base font-medium text-white/80 italic leading-8"
                >
                  "Technology should not complicate life — it should bring clarity, peace, and control
                  back to the user."
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values / Pillars */}
      <section data-testid="founder-values" className="mx-auto max-w-5xl px-6 py-20">
        <div className="mb-10">
          <p className="text-xs font-medium uppercase tracking-[0.25em] text-amber-400">
            Core Principles
          </p>
          <h2 className="mt-3 text-2xl font-bold tracking-tight md:text-3xl">
            What drives Perfect Peace Enterprise
          </h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              title: "Purpose-Driven",
              text: "Every product starts with a real problem, not a market trend.",
            },
            {
              title: "Lived Experience",
              text: "Built by someone who has been in the field, not just behind a screen.",
            },
            {
              title: "Serve & Protect",
              text: "Technology that empowers users and safeguards what matters most.",
            },
            {
              title: "Clarity Over Complexity",
              text: "Solutions that simplify life, never complicate it.",
            },
          ].map((item, i) => (
            <div
              key={i}
              data-testid={`value-card-${i}`}
              className="rounded-2xl border border-white/[0.08] bg-white/[0.03] p-6 transition-all hover:border-amber-400/15"
            >
              <div className="mb-3 text-xs font-mono text-amber-400/50">
                {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="text-base font-semibold text-white">{item.title}</h3>
              <p className="mt-2 text-sm leading-6 text-slate-400">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-white/[0.06] bg-white/[0.015]">
        <div className="mx-auto max-w-5xl px-6 py-16 text-center">
          <img
            src={LOGO_URL}
            alt="Perfect Peace Enterprise"
            className="mx-auto w-20 h-20 object-contain mb-6"
          />
          <h2 className="text-2xl font-bold tracking-tight md:text-3xl">
            Explore the Product Ecosystem
          </h2>
          <p className="mt-3 max-w-lg mx-auto text-sm text-slate-400 leading-7">
            See the full lineup of apps built by Richard and the Perfect Peace Enterprise team.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/#apps"
              data-testid="founder-explore-apps"
              className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-950 transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              View All Apps
              <ArrowRight size={16} />
            </Link>
            <a
              href="/#contact"
              data-testid="founder-contact-btn"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-white transition-all hover:bg-white/5"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.06] bg-slate-950">
        <div className="mx-auto max-w-7xl px-6 py-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Logo" className="w-8 h-8 object-contain" />
            <div>
              <div className="text-base font-semibold text-white">
                Perfect Peace Enterprise
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                Building digital products for real-world impact.
              </p>
            </div>
          </div>
          <Link
            to="/"
            className="text-sm text-slate-500 hover:text-white transition-colors"
          >
            Back to Home
          </Link>
        </div>
      </footer>
    </div>
  );
}
